import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getCompanyMSTeamMemberListDataByCMSId } from '../companyMSTeamMembers/mockData'
import { getCompanyMSContactListDataByCMSId } from '../companyMSContacts/mockData'
import { getCompanyMSDocumentListDataByCMSId } from '../companyMSDocuments/mockData'
import { getCompanyMSNoteListDataByCMSId } from '../companyMSNotes/mockData'
import {
  getCompanyMSServiceListDataByCMSId,
  _getCMSSListDataByCMSId,
} from '../companyMSServices/mockData'
import {
  getCompanyDataById,
  getCompanyListData,
  _getCSelectItemDataById,
  getCompanyOverviewData,
} from '../companies/mockData'
import { getLocationListDataByCompanyId } from '../locations/mockData'
import {
  getProjectListData,
  _getProjSelectItemDataById,
} from '../projects/mockData'
import { propagateCompanyMSDataToCompany } from '~/cynqueModules/dataObjects/CompanyMasterService'
import {
  getCompanyMasterServiceStatusById,
  CompanyMasterServicesStatusIds,
} from '@/services/selectLists/companyMasterServicesStatii'
import { assertIsNotUndefined, assertIsString } from '~/types/models'
import type {
  ICompany,
  ICompanyMasterService,
  ICompanyMSService,
} from '~/types/company/models'
import type {
  IEntitySelectItem,
  IEntitySelectItemWithOptionalAvatar,
} from '~/types/models'

const mn = '[api/msw/api/companyMasterServices/mockData.ts]'

const companyMasterServiceListData =
  undefined as unknown as ICompanyMasterService[]

export const getCompanyMasterServiceListData = getListDataFunctionFactory<
  ICompanyMasterService[]
>({
  listData: companyMasterServiceListData,
  storageKey: 'companymasterservices',
  getStartingListData: getStartingCompanyMasterServiceListData,
  forOfFunction: async (cms) => {
    cms.companyMSServices = await getCompanyMSServiceListDataByCMSId(cms.id)
    cms.companyMSTeamMembers = await getCompanyMSTeamMemberListDataByCMSId(
      cms.id
    )
    cms.companyMSContacts = await getCompanyMSContactListDataByCMSId(cms.id)
    cms.companyMSDocuments = await getCompanyMSDocumentListDataByCMSId(cms.id)
    cms.companyMSNotes = await getCompanyMSNoteListDataByCMSId(cms.id)
  },
})

export async function getCompanyMasterServiceDataById(id: string) {
  const cmsList = await getCompanyMasterServiceListData()
  if (!cmsList) {
    throw new Error(`getCompanyMasterServiceListData returned undefined/null`)
  }
  const companyMasterService = cmsList.find(
    (cms: ICompanyMasterService) => cms.id === id
  )
  return companyMasterService
}

export async function getCompanyMasterServiceSelectItemDataById(
  id: string
): Promise<IEntitySelectItem> {
  const cms = await getCompanyMasterServiceDataById(id)
  if (!cms) {
    throw new Error(
      `getCompanyMasterServiceDataById returned undefined/null for id '${id}'`
    )
  }
  return { id: cms.id, label: cms.descr, hrid: cms.hrid }
}

export async function getCompanyMasterServiceListDataByCompanyId(
  id: string
): Promise<ICompanyMasterService[]> {
  const list = await getCompanyMasterServiceListData()
  if (!list) {
    throw new Error(`getCompanyMasterServiceListData returned undefined/null`)
  }
  const cmsList = list.filter((cms) => cms.company.id === id) ?? []
  return cmsList
}

export async function getPotentialLocationSelectItemListDataByCMSId(
  id: string
) {
  const companyMasterService = await getCompanyMasterServiceDataById(id)
  if (!companyMasterService) {
    throw new Error(`getCompanyMasterServiceDataById returned undefined/null`)
  }
  const companyId = companyMasterService.company.id
  if (typeof companyId !== 'string') {
    throw new TypeError(`companyId is not type 'string'`)
  }
  const locationListData = await getLocationListDataByCompanyId(companyId)
  return locationListData.map((loc) => {
    return { id: loc.id, label: loc.nickname }
  })
}

export async function getCompanyDataByCompanyMasterServiceId(id: string) {
  const cms = await getCompanyMasterServiceDataById(id)
  if (!cms) {
    throw new Error(`getCompanyMasterSServiceDataById returned undefined/null`)
  }
  assertIsString(cms.company.id)
  const company = await getCompanyDataById(cms.company.id)
  if (!cms) {
    throw new Error(`getCompanyMasterSServiceDataById returned undefined/null`)
  }
  return company
}

export async function getSupplierListDataByCompanyMasterServiceId(
  id: string
): Promise<IEntitySelectItemWithOptionalAvatar[]> {
  const fn = `${mn} getSupplierDataByCompanyMasterServiceId |`
  const cms = await getCompanyMasterServiceDataById(id)
  if (!cms) {
    throw new Error(`getCompanyMasterSServiceDataById returned undefined/null`)
  }
  if (!cms.companyMSServices) {
    console.debug(`${fn} cms has no cmss. Can't get cmss supplier listData'`)
    return []
  }
  return cms.companyMSServices.reduce(
    (
      unique: IEntitySelectItemWithOptionalAvatar[],
      cmss: ICompanyMSService
    ) => {
      if (cmss.supplier && cmss.doInclude) {
        if (!unique.some((obj) => obj.id === cmss.supplier?.id)) {
          unique.push(cmss.supplier)
        }
      }
      return unique
    },
    []
  )
}

export async function propagateCompanyMasterServiceAdditions({
  newEntityData,
}: {
  newEntityData: ICompanyMasterService | ICompanyMasterService[]
}) {
  const fn = `${mn} propagateCompanyMasterServiceAdditions |`
  if (!newEntityData) {
    console.debug(`${fn} newEntityData is falsey. Nothing to propagate.`)
    return
  }
  // ⚠ ROW **OR** ARRAY, like propagateCompanyMSServiceOrServicesAdditions.
  //
  // `addNewEntityDataToEntityListData` hands `postProcessOnceForAllFn` whatever
  // it was given, and for a multi-row create that is the whole ARRAY
  // (mockDataCommon.ts: `await postProcessOnceForAllFn({ newEntityData })`).
  // Reading `.company.id` off an array yields undefined and throws
  // "Cannot read properties of undefined" — inside the write phase, so on the
  // /api/transactions route it would roll the whole transaction back and fail every
  // completion that creates a company master service.
  //
  // ONE row is enough: the propagation below re-reads the entire company
  // overview and recomputes from it, so it is company-wide rather than
  // per-row. Running it once per row would repeat identical work.
  const companyMasterService = Array.isArray(newEntityData)
    ? newEntityData[0]
    : newEntityData
  if (!companyMasterService) {
    console.debug(`${fn} no rows to propagate.`)
    return
  }
  _propagateCompanyMasterServiceAdditionsChangesOrDeletions({
    companyMasterService,
  })
}

export async function propagateCompanyMasterServiceChanges({
  targetItem,
}: {
  targetItem: ICompanyMasterService
}) {
  const fn = `${mn} propagateCompanyMasterServiceChanges |`
  if (!targetItem) {
    console.debug(`${fn} updatedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateCompanyMasterServiceAdditionsChangesOrDeletions({
    companyMasterService: targetItem,
  })
}

export async function propagateCompanyMasterServiceDeletions({
  deletedEntityData,
}: {
  deletedEntityData: ICompanyMasterService
}) {
  const fn = `${mn} propagateCompanyMasterServiceDeletions |`
  if (!deletedEntityData) {
    console.debug(`${fn} deletedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateCompanyMasterServiceAdditionsChangesOrDeletions({
    companyMasterService: deletedEntityData,
  })
}

async function _propagateCompanyMasterServiceAdditionsChangesOrDeletions({
  companyMasterService,
}: {
  companyMasterService: ICompanyMasterService
}) {
  const fn = `${mn} _propagateCompanyMasterServiceAdditionsChangesOrDeletions |`
  console.debug(`${fn} running`)
  if (!companyMasterService) {
    console.debug(`${fn} companyMasterService is falsey. Nothing to propagate.`)
    return
  }
  const company = await getCompanyDataById(companyMasterService.company.id)
  if (!company) {
    throw new Error(`getCompanyDataById returned undefined/null`)
  }
  const overviewData = await getCompanyOverviewData(company.id)
  assertIsNotUndefined(overviewData)
  console.debug(`${fn} overviewData:`, overviewData)

  propagateCompanyMSDataToCompany({
    callingFn: fn,
    company: overviewData.company,
    masterServices: overviewData.companyMasterServices,
    setUpCompany: function _setUpCompany(company: ICompany) {},
  })
  console.debug(`${fn} company after propagation:`, overviewData.company)

  const companyList = await getCompanyListData()
  if (!companyList) {
    throw new Error(`getCompanyListData returned undefined/null`)
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'companies',
    listData: companyList,
    callingFn: fn,
  })
}

export async function getStartingCompanyMasterServiceListData(): Promise<
  ICompanyMasterService[]
> {
  const cList = await getCompanyListData()
  const pList = await getProjectListData()
  return [
    {
      id: 'XXCMS0001',
      hrid: 'CMS0001',
      descr: 'DIA',
      company: _getCSelectItemDataById('XXC0001', cList),
      origin: {
        id: 1,
        label: 'New',
      },
      project: _getProjSelectItemDataById('XXP0002', pList),
      term: 36,
      numLocations: 2,
      numServices: 8,
      mcmssStatusCounts: [],
      status: getCompanyMasterServiceStatusById(
        CompanyMasterServicesStatusIds.READY_FOR_CONTRACTS
      )!,
      version: '1',
      canBeDeleted: false,
      companyMSServices: [], // Will be set by forOfFn  _getOMSSListDataByCMSId('XXCMS0001', mcmssList),
      companyMSTeamMembers: [], // Will be set by forOfFn  await getCompanyMSTeamMemberListDataByCMSId(
      companyMSContacts: [], // Will be set by forOfFn
      companyMSDocuments: [], // Will be set by forOfFn
    },
    {
      id: 'XXCMS0002',
      hrid: 'CMS0002',
      descr: 'UCaaS',
      company: _getCSelectItemDataById('XXC0001', cList),
      origin: {
        id: 1,
        label: 'New',
      },
      term: undefined,
      accountNum: '293445',
      numLocations: 0,
      numServices: 0,
      project: _getProjSelectItemDataById('XXP0001', pList),
      status: getCompanyMasterServiceStatusById(
        CompanyMasterServicesStatusIds.NO_SERVICES
      ),
      version: '1',
      canBeDeleted: false,
      companyMSServices: [], // Will be set by forOfFn  _getOMSSListDataByCMSId('XXCMS0001', mcmssList),
      companyMSTeamMembers: [], // Will be set by forOfFn  await getCompanyMSTeamMemberListDataByCMSId(
      companyMSContacts: [], // Will be set by forOfFn
      companyMSDocuments: [], // Will be set by forOfFn
    },
    {
      id: 'XXCMS0003',
      hrid: 'CMS0003',
      descr: 'POTS',
      company: _getCSelectItemDataById('XXC0001', cList),
      origin: {
        id: 1,
        label: 'New',
      },
      term: 36,
      numLocations: 2,
      numServices: 10,
      project: _getProjSelectItemDataById('XXP0003', pList),
      status: getCompanyMasterServiceStatusById(
        CompanyMasterServicesStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
      companyMSServices: [], // Will be set by forOfFn  _getOMSSListDataByCMSId('XXCMS0001', mcmssList),
      companyMSTeamMembers: [], // Will be set by forOfFn  await getCompanyMSTeamMemberListDataByCMSId(
      companyMSContacts: [], // Will be set by forOfFn
      companyMSDocuments: [], // Will be set by forOfFn
    },
    {
      // An installed Cogent DIA kit (see _installedInternetKitRows in
      // companyMSServices/mockData.ts): the only installed kit in the mock.
      id: 'XXCMS0004',
      hrid: 'CMS0004',
      descr: 'Internet',
      company: _getCSelectItemDataById('XXC0001', cList),
      origin: {
        id: 1,
        label: 'New',
      },
      term: 36,
      numLocations: 1,
      numServices: 1,
      project: _getProjSelectItemDataById('XXP0002', pList),
      status: getCompanyMasterServiceStatusById(
        CompanyMasterServicesStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
      companyMSServices: [], // Will be set by forOfFn
      companyMSTeamMembers: [], // Will be set by forOfFn
      companyMSContacts: [], // Will be set by forOfFn
      companyMSDocuments: [], // Will be set by forOfFn
    },
  ]
}
